/**
 * Hook for fetching notifications for user membership
 * Combines public notifications and membership-targeted notifications
 */

import { useCallback, useMemo } from 'react';
import type { DrupalNotification, NotificationQueryParams, UseNotificationsResult } from '../types';
import { mergeNotifications } from '../utils';

/**
 * Custom hook dependency - must be provided by the consuming application
 * This allows the package to remain decoupled from specific query implementations
 */
export interface UseDrupalCollectionHook {
  <T = Record<string, unknown>>(
    resourceType: string,
    searchParams?: Record<string, string> | undefined
  ): {
    items: T[];
    isLoading: boolean;
    error: string | null;
  };
}

/**
 * Hook options for notifications fetching
 */
export interface UseNotificationsOptions {
  /** User's membership type ID for targeted notifications */
  membershipTypeId?: string | null;
  /** Assembly IDs for group-specific notifications */
  assemblyIds?: string[];
  /** Application IDs for application-specific notifications */
  applicationIds?: string[];
  /** Maximum number of notifications to fetch */
  maxNotifications?: number;
  /** Whether to include public notifications */
  includePublic?: boolean;
  /** Custom Drupal collection hook implementation */
  useDrupalCollection: UseDrupalCollectionHook;
}

/**
 * Creates base query parameters for notifications
 */
function createBaseParams(maxNotifications: number = 5): URLSearchParams {
  const sp = new URLSearchParams();

  sp.set(
    'fields[node--notifications]',
    [
      'title',
      'field_html',
      'created',
      'changed',
      'field_start',
      'field_end',
      'field_notified_users',
      'field_notif_memberships',
      'field_assembly',
      'field_applications',
      'field_variant',
    ].join(',')
  );
  sp.set('fields[taxonomy_term--membership_type]', 'name');
  sp.set('fields[taxonomy_term--groups]', 'name');
  sp.set('fields[node--application]', 'title');
  sp.set('include', 'field_notif_memberships,field_assembly,field_applications');
  sp.set('filter[status]', '1');
  sp.set('sort', '-created');
  sp.set('page[limit]', String(maxNotifications));

  return sp;
}

/**
 * Hook to fetch notifications for the user's membership
 * Fetches both public notifications and membership/assembly/application-targeted notifications
 */
export function useNotificationsForMembership(
  options: UseNotificationsOptions
): UseNotificationsResult {
  const {
    membershipTypeId,
    assemblyIds = [],
    applicationIds = [],
    maxNotifications = 5,
    includePublic = true,
    useDrupalCollection,
  } = options;

  const baseParams = useCallback(
    () => createBaseParams(maxNotifications),
    [maxNotifications]
  );

  // Public notifications (no target memberships)
  const publicParams = useMemo((): Record<string, string> | undefined => {
    if (!includePublic) return undefined;

    const sp = baseParams();
    sp.set('filter[public][condition][path]', 'field_notif_memberships');
    sp.set('filter[public][condition][operator]', 'IS NULL');
    return Object.fromEntries(sp.entries());
  }, [baseParams, includePublic]);

  // Membership-targeted notifications
  const membershipParams = useMemo((): Record<string, string> | undefined => {
    if (!membershipTypeId) return undefined;

    const sp = baseParams();
    sp.set('filter[field_notif_memberships.id]', membershipTypeId);
    return Object.fromEntries(sp.entries());
  }, [membershipTypeId, baseParams]);

  // Assembly-targeted notifications
  const assemblyParams = useMemo((): Record<string, string> | undefined => {
    if (assemblyIds.length === 0) return undefined;

    const sp = baseParams();

    if (assemblyIds.length === 1) {
      sp.set('filter[field_assembly.id]', assemblyIds[0]);
    } else {
      sp.set('filter[field_assembly.id][operator]', 'IN');
      assemblyIds.forEach((assemblyId, index) => {
        sp.set(`filter[field_assembly.id][value][${index}]`, assemblyId);
      });
    }

    return Object.fromEntries(sp.entries());
  }, [assemblyIds, baseParams]);

  // Application-targeted notifications
  const applicationParams = useMemo((): Record<string, string> | undefined => {
    if (applicationIds.length === 0) return undefined;

    const sp = baseParams();

    if (applicationIds.length === 1) {
      sp.set('filter[field_applications.id]', applicationIds[0]);
    } else {
      sp.set('filter[field_applications.id][operator]', 'IN');
      applicationIds.forEach((applicationId, index) => {
        sp.set(`filter[field_applications.id][value][${index}]`, applicationId);
      });
    }

    return Object.fromEntries(sp.entries());
  }, [applicationIds, baseParams]);

  // Fetch public notifications
  const {
    items: publicItems,
    isLoading: loadingPublic,
    error: errorPublic,
  } = useDrupalCollection<DrupalNotification>(
    'node--notifications',
    publicParams
  );

  // Fetch membership-targeted notifications
  const {
    items: membershipItems,
    isLoading: loadingMembership,
    error: errorMembership,
  } = useDrupalCollection<DrupalNotification>(
    'node--notifications',
    membershipParams
  );

  // Fetch assembly-targeted notifications
  const {
    items: assemblyItems,
    isLoading: loadingAssembly,
    error: errorAssembly,
  } = useDrupalCollection<DrupalNotification>(
    'node--notifications',
    assemblyParams
  );

  // Fetch application-targeted notifications
  const {
    items: applicationItems,
    isLoading: loadingApplication,
    error: errorApplication,
  } = useDrupalCollection<DrupalNotification>(
    'node--notifications',
    applicationParams
  );

  // Merge and deduplicate notifications
  const items = useMemo(
    () => mergeNotifications(publicItems, membershipItems, assemblyItems, applicationItems),
    [publicItems, membershipItems, assemblyItems, applicationItems]
  );

  // Aggregate loading and error states
  const isLoading =
    loadingPublic ||
    (membershipTypeId ? loadingMembership : false) ||
    (assemblyIds.length > 0 ? loadingAssembly : false) ||
    (applicationIds.length > 0 ? loadingApplication : false);

  const error = errorPublic || errorMembership || errorAssembly || errorApplication
    ? new Error(errorPublic || errorMembership || errorAssembly || errorApplication || 'Unknown error')
    : null;

  return { items, isLoading, error };
}

